/**
 * src/components/shared/AddFab.jsx — the ONE floating "+" action button.
 *
 * CEO 2026-06-12: every add FAB must look identical, "even if I change it later"
 * — so the style lives here once and every view renders this component instead of
 * re-styling a <button>. Change the look in ONE place and all FABs follow.
 *
 * Canonical style = the Focus add-FAB: 56px round, navy→blue gradient, var(--inv)
 * icon, soft navy shadow, lifted above the bottom nav + iOS home-indicator inset.
 *
 * Positioning: `positioned` (default) fixes it bottom-right, clear of the tab bar.
 * Pass `positioned={false}` to drop it into a parent-managed container (e.g. a
 * column that stacks several FABs).
 */

import { Plus } from 'lucide-react';

const FAB_STYLE = {
  width: 56, height: 56, minHeight: 44, borderRadius: '50%',
  background: 'linear-gradient(135deg, var(--navy-surface), var(--blue))',
  color: 'var(--inv)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  border: 'none', cursor: 'pointer',
  boxShadow: '0 4px 16px rgba(28,63,117,0.4)',
  transition: 'transform 150ms var(--ease)',
};

// Clears the bottom tab bar (height calc(56px + safe-area)) with a 20px gap.
const POSITIONED = {
  position: 'fixed', bottom: 'calc(76px + env(safe-area-inset-bottom, 0px))',
  right: 20, zIndex: 90,
};

/**
 * @param {object} props
 * @param {() => void} props.onClick
 * @param {string} props.ariaLabel              accessible label (required)
 * @param {string} [props.testId='add-fab']     data-testid (view-component-action)
 * @param {string} [props.title]                native tooltip
 * @param {React.ComponentType} [props.icon=Plus]
 * @param {boolean} [props.positioned=true]     fixed bottom-right vs parent-placed
 * @param {object} [props.style]                extra style overrides (e.g. zIndex)
 */
export default function AddFab({ onClick, ariaLabel, testId = 'add-fab', title, icon: Icon = Plus, positioned = true, style }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-label={ariaLabel}
      title={title}
      onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.92)'; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      style={{ ...FAB_STYLE, ...(positioned ? POSITIONED : {}), ...style }}
    >
      <Icon size={24} strokeWidth={2} />
    </button>
  );
}
